import { useMemberFeatures } from "@/api/me";
import { conversationsQuery } from "@/api/messages";
import { PageInset } from "@/components/PageInset";
import { PurchaseDialog } from "@/components/PurchaseDialog";
import { ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { useHasUnreads } from "@/hooks/useHasUnreads";
import { seo } from "@/utils/seo";
import { locale } from "@common/utils/i18n";
import { Avatar, Box, Center, Flex, QueryHandler, Skeleton, Text } from "@givenwell/components";
import { ChatBubbleTextSquareIcon } from "@givenwell/core-solid-icons";
import { MemberConversation } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, Link, Outlet, useMatches, useNavigate } from "@tanstack/react-router";
import { fallback, object, optional, string } from "valibot";

export const Route = createFileRoute("/_auth/_app/messages")({
  validateSearch: object({
    showReceipt: fallback(optional(string()), undefined),
  }),
  component: Layout,
  head: () => ({ meta: seo({ title: "Messages" }) }),
});

const route = getRouteApi("/_auth/_app/messages");

function Layout() {
  const { showReceipt } = route.useSearch();
  const navigate = useNavigate();

  const query = useQuery(conversationsQuery);
  useHasUnreads(); // Socket will trigger updates automatically

  const conversationId = useMatches({
    select: matches => {
      const conversationMatch = matches.find(match => match.routeId === "/_auth/_app/messages/$conversationId");
      if (!conversationMatch) return undefined;
      if ("conversationId" in conversationMatch.params) return conversationMatch.params.conversationId;
      return;
    },
  });
  const isWithinConversation = !!conversationId;
  const caps = useMemberFeatures();

  const empty = query.isSuccess && query.data.length === 0;

  let description = "Communication with Suppliers will appear here for inquiry or each purchase you make.";
  if (caps.hasEap) {
    description = "You can message practitioners for support, or communicate with other people you have booked.";
  }
  if (caps.hasAllowance) {
    description = "Communication with Suppliers will appear here for inquiry or each purchase you make.";
  }

  if (empty) {
    return (
      <PageWrapper>
        <Flex css={{ flexDirection: "column", flex: "1 0 0px", overflow: "hidden" }}>
          <Flex
            css={{
              items: "center",
              height: 64,
              justify: "center",
              align: "center",
              backgroundColor: colors.cream,
              borderBottom: `1px solid rgba(0, 0, 0, 0.05)`,

              width: "100%",
              maxW: 960,
              mx: "auto",

              display: "flex",
              "@lg": {
                borderBottom: "none",
              },
            }}
          >
            <Text css={{ fontScale: "lg", weight: 500, fontFamily: fonts.ftHabit }}>Messages</Text>
          </Flex>
          <Flex
            css={{
              flex: "1 0 0px",
              justify: "center",
              items: "center",
              flexDir: "column",
              align: "center",
            }}
          >
            <Flex
              css={{
                flexDirection: "column",
                width: "100%",
                maxW: 540,
                gap: 40,
                pl: "max(16px, var(--safe-area-inset-left))",
                pr: "max(16px, var(--safe-area-inset-right))",
                "@md": {
                  maxW: 500,
                },
              }}
            >
              <Box css={{ spaceY: 20 }}>
                <Center
                  css={{
                    backgroundColor: colors.warmGrey,
                    width: "100%",
                    w: 72,
                    h: 72,
                    mx: "auto",
                    borderRadius: "50%",
                  }}
                >
                  <ChatBubbleTextSquareIcon width="28" height="28" />
                </Center>
                <Box css={{ px: 20, width: "100%", maxW: 320, mx: "auto" }}>
                  <Text css={{ fontScale: "md", weight: 500, mb: 4 }}>No messages</Text>
                  <Text css={{ fontScale: "sm", color: colors.text2 }}>{description}</Text>
                </Box>
              </Box>
              <Flex
                css={{
                  flexDir: "column",
                  gap: 16,
                  px: 16,
                  "@md": { display: "grid", gridCols: 2 },
                }}
              >
                {caps.hasAllowanceMarketplace ? (
                  <>
                    <ButtonLink variant="filled" size="lg" color="primary" to="/marketplace">
                      Go Shopping
                    </ButtonLink>
                  </>
                ) : null}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </PageWrapper>
    );
  }

  return (
    <>
      <Flex css={{ flex: "1 0 0px", overflow: "hidden" }}>
        <Flex
          css={{
            flexDirection: "column",
            flex: "2 0 0px",
            overflow: "hidden",
            display: isWithinConversation ? "none" : "block",
            "@sm": { display: "block" },
            "@lg": {
              flex: "1 0 0px",
            },
          }}
        >
          <Flex
            css={{
              items: "center",
              height: 53,
              ninHeight: 53,
              mt: "var(--safe-area-inset-top)",
              justify: "center",
              align: "center",
              backgroundColor: colors.cream,
              borderBottom: `1px solid rgba(0, 0, 0, 0.05)`,

              width: "100%",
              maxW: 1200,
              mx: "auto",
              mb: 16,
              px: 16,

              display: isWithinConversation ? "none" : "flex",
              "@sm": {
                display: "flex",
              },
              "@lg": {
                borderBottom: "none",
                justify: "flex-start",
              },
            }}
          >
            <Text css={{ fontScale: "lg", weight: 500, fontFamily: fonts.ftHabit }}>Messages</Text>
          </Flex>
          <Flex
            css={{
              flex: "1 0 0px",
              width: "100%",
              overflow: "hidden",
              overflowY: "auto",
              flexDirection: "column",
            }}
          >
            <PageInset>
              <QueryHandler
                query={query}
                empty={() => <Box css={{ px: 16 }}></Box>}
                success={({ data }) => (
                  <>
                    {data.map(conversation => (
                      <ConversationCard
                        key={conversation.id}
                        conversation={conversation}
                        active={conversation.id === conversationId}
                      />
                    ))}
                  </>
                )}
                pending={() => (
                  <>
                    {[1, 2, 3, 4, 5].map(i => (
                      <ConversationCardSkeleton key={i} />
                    ))}
                  </>
                )}
              />
            </PageInset>
          </Flex>
        </Flex>
        {/* <Box css={{ flex: "1 0 0px" }}> */}
        <Box
          css={{
            flex: "3 0 0px",
            display: isWithinConversation ? "block" : "none",
            "@sm": { display: "block" },
            borderLeft: `1px solid ${colors.outline2}`,
            "@lg": {
              flex: "2 0 0px",
            },
            "@xl": {
              flex: "3 0 0px",
            },
          }}
        >
          <Outlet />
        </Box>
        {/* </Box> */}
      </Flex>
      <PurchaseDialog
        purchaseId={showReceipt}
        open={!!showReceipt}
        justPurchased
        onOpenChange={open => {
          if (!open) {
            navigate({ to: "/messages", search: { showReceipt: undefined } });
          }
        }}
      />
    </>
  );
}

const messageTimeFormatter = new Intl.DateTimeFormat(locale, {
  month: "short",
  day: "numeric",
});

function ConversationCard({ conversation, active }: { conversation: MemberConversation; active?: boolean }) {
  return (
    <CardRoot
      to="/messages/$conversationId"
      data-active={active ? "" : undefined}
      params={{
        conversationId: conversation.id,
      }}
      style={{
        // backgroundColor: conversation.seen.me ? undefined : colors.cyanLight,
      }}
    >
      <Avatar
        size={40}
        name={conversation.supplier_name}
        id={conversation.supplier_id}
        src={conversation.supplier_profile_url || undefined}
      />
      <Box css={{ spaceY: 4, flex: "1 0 0px" }}>
        <Flex css={{ items: "baseline" }}>
          <Text
            css={{
              fontScale: "sm",
              lineHeight: "20px",
              weight: conversation.seen.me ? 400 : 500,
              flex: "1 0 0px",
            }}
          >
            {conversation.supplier_name}
          </Text>
          <Text
            css={{
              fontScale: "xs",
              lineHeight: "16px",
              weight: 400,
              color: colors.text2,
            }}
          >
            {formatMessageTime(conversation.most_recent_message?.sent || "")}
          </Text>
        </Flex>
        <Flex css={{ items: "center" }}>
          <Text
            css={{
              fontScale: "xs",
              lineClamp: 1,
              flex: "1 0 0px",
              weight: conversation.seen.me ? 400 : 500,
              color: conversation.seen.me ? colors.text2 : colors.text,
            }}
          >
            {conversation.most_recent_message?.sender === "member" && "You: "}
            {conversation.most_recent_message?.text_preview}
          </Text>
          {!conversation.seen.me && (
            <Box
              css={{
                backgroundColor: colors.navy,
                size: 8,
                rounded: 8,
                ml: 12,
              }}
            />
          )}
        </Flex>
      </Box>
    </CardRoot>
  );
}

function formatMessageTime(time: string) {
  const date = time ? new Date(time) : new Date();
  const now = new Date();
  const sameDay =
    date.getDate() === now.getDate() && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  if (sameDay) {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }
  const sameWeek = date.getTime() > now.getTime() - 7 * 24 * 60 * 60 * 1000;
  if (sameWeek) {
    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
    }).format(date);
  }
  return messageTimeFormatter.format(date);
}

function ConversationCardSkeleton() {
  return (
    <CardSkeletonRoot>
      <Skeleton css={{ size: 40, rounded: 9999 }} />
      <Box css={{ flex: 1, spaceY: 4 }}>
        <Flex css={{ gap: 8 }}>
          <Skeleton css={{ h: 16, mb: 4 }}>
            <Text css={{ fontScale: "sm", lineHeight: "20px", weight: 400 }}>Hot Yoga Dunedin</Text>
          </Skeleton>
        </Flex>

        <Skeleton css={{ w: "100%" }}>
          <Text css={{ fontScale: "sm", lineClamp: 1 }}>Oh hi</Text>
        </Skeleton>
      </Box>
    </CardSkeletonRoot>
  );
}

const CardSkeletonRoot = styled("div", {
  d: "flex",
  height: 80,
  items: "center",
  px: 16,
  gap: 12,
});

const CardRoot = styled(Link, {
  d: "flex",
  // height: 80,
  items: "center",
  px: 8,
  py: 12,
  gap: 12,
  borderRadius: 9,

  "&:hover": {
    backgroundColor: colors.cyanLight,
  },

  "&[data-active]": {
    backgroundColor: colors.cyanLight,
    "&:hover": {
      backgroundColor: colors.cyanLight,
    },
  },
}) as unknown as typeof Link;
